import type { AxiosError } from 'axios';

/** Statuses axios-retry treats as transient: rate limit and gateway errors. */
const RETRYABLE_STATUSES = new Set([429, 502, 503, 504]);

/** Methods that are safe to send twice. */
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options']);

/**
 * Webhook API routes (BV-2186): endpoints, health, deliveries, operations —
 * customer (`/organizations/{id}/...`) and admin (`/admin/...`) alike.
 */
const WEBHOOK_API_PATH = /\/webhook-(?:endpoints|endpoint-health|deliveries|operations)(?:[/?#]|$)/;

/**
 * The 503 messages compass returns when the webhook dispatcher (iris) is down
 * or not configured. They are not gateway blips: retrying only delays the
 * inline "temporarily unavailable" state and fires the global busy toast.
 */
const DISPATCHER_UNAVAILABLE_MESSAGES = [
  'webhook dispatcher is unavailable',
  'webhook dispatcher is not configured',
];

function responseMessage(data: unknown): string {
  if (data && typeof data === 'object') {
    const { error, message } = data as { error?: unknown; message?: unknown };
    if (typeof error === 'string') return error;
    if (typeof message === 'string') return message;
  }
  return '';
}

function isDispatcherUnavailable(data: unknown): boolean {
  const message = responseMessage(data).toLowerCase();
  return DISPATCHER_UNAVAILABLE_MESSAGES.some(m => message.includes(m));
}

/** The narrower policy for webhook routes; see shouldRetryRequest. */
function shouldRetryWebhookRequest(method: string, status: number, data: unknown): boolean {
  if (!IDEMPOTENT_METHODS.has(method)) return false;
  if (status === 429) return false;
  return !(status === 503 && isDispatcherUnavailable(data));
}

/**
 * Whether axios-retry should retry a failed request.
 *
 * Unchanged for every route but the webhook API: 429/502/503/504 are retried.
 * For webhook routes:
 *  - only idempotent methods are retried — test events, retries, bulk retries
 *    and replays may already have been acted on when compass answers 503, and
 *    each test-event retry would mint a new event;
 *  - 429 is never retried — it means "selection too large" or the per-hour
 *    bulk-retry/replay limit, which a retry cannot fix and only burns quota;
 *  - a dispatcher-down 503 is not retried (the UI shows "—" / an inline
 *    error instead); a gateway 503 on a GET still is.
 */
export function shouldRetryRequest(error: Pick<AxiosError, 'config' | 'response'>): boolean {
  const status = error.response?.status;
  if (status === undefined || !RETRYABLE_STATUSES.has(status)) return false;
  if (!WEBHOOK_API_PATH.test(error.config?.url ?? '')) return true;
  const method = (error.config?.method ?? 'get').toLowerCase();
  return shouldRetryWebhookRequest(method, status, error.response?.data);
}
